import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Megaphone, QrCode, Copy, Check, Download, BarChart3, RefreshCw, Lock } from "lucide-react";
import { homesSearch, homesMarketingKit, homesMarketingStats, homesSetQrMode } from "@/lib/api";
import { useAuth, login } from "@/context/AuthContext";

export default function MarketingCenter() {
  const { user, loading: authLoading } = useAuth();
  const [listings, setListings] = useState([]);
  const [sel, setSel] = useState("");
  const [kit, setKit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [mode, setMode] = useState("listing");

  const switchMode = async (m) => {
    if (!kit?.listing?.id) return;
    setMode(m);
    try { await homesSetQrMode(kit.listing.id, m); toast.success(`QR now points to: ${kit.modes[m]}`); }
    catch { toast.error("Couldn't switch destination"); }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    homesSearch({ page_size: 24 }).then((d) => setListings(d.listings || [])).catch(() => {});
  }, []);

  const generate = async () => {
    if (!sel) { toast.error("Pick a property first"); return; }
    setBusy(true); setKit(null); setStats(null);
    try {
      const res = await homesMarketingKit(sel);
      setKit(res);
      setMode(res.qr_mode || "listing");
      if (!res.link?.ok) toast.message(res.link?.message || "Branded link needs a live production URL.");
    } catch { toast.error("Couldn't generate the kit."); }
    finally { setBusy(false); }
  };

  const loadStats = async () => {
    if (!kit?.link?.slug) return;
    setStats("loading");
    try { const s = await homesMarketingStats(kit.link.slug); setStats(s); }
    catch { setStats({ ok: false }); }
  };

  const copy = () => {
    if (!kit?.link?.short_url) return;
    navigator.clipboard?.writeText(kit.link.short_url);
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };

  if (!user && authLoading) return <Center><Loader2 className="h-8 w-8 animate-spin text-[#2563EB]" /></Center>;
  if (!user) return (
    <Center><Lock className="h-10 w-10 text-slate-300" /><h1 className="mt-4 text-2xl font-black text-[#0F172A]">Sign in to use the Marketing Center</h1>
      <button onClick={() => login("/homes/marketing")} className="mt-5 rounded-full bg-[#2563EB] px-6 py-3 text-sm font-bold text-white hover:bg-[#1D4ED8]" data-testid="marketing-signin">Sign in</button></Center>
  );

  const link = kit?.link;
  return (
    <div className="mx-auto max-w-[1000px] px-4 py-10" data-testid="marketing-center">
      <Link to="/homes/agent-portal" className="mb-4 inline-block text-sm text-[#2563EB]">← Agent Portal</Link>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#2563EB]"><Megaphone className="h-4 w-4" /> Marketing Center</div>
      <h1 className="mt-1 text-3xl font-black text-[#0F172A]">Property QR & link kit</h1>
      <p className="mt-1 text-slate-500">Turn any listing into a branded SpLink QR code for yard signs, flyers and open houses — with scan analytics.</p>

      <div className="mt-6 flex flex-wrap items-end gap-3 rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex-1">
          <label className="mb-1.5 block text-xs font-bold uppercase tracking-widest text-slate-500">Choose a property</label>
          <select value={sel} onChange={(e) => setSel(e.target.value)} className="w-full rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-[#0F172A] outline-none focus:border-[#2563EB]" data-testid="marketing-listing-select">
            <option value="">Select a listing…</option>
            {listings.map((l) => <option key={l.id} value={l.id}>{`${l.address || l.title} — ${l.city_name}, ${l.state_name}`}</option>)}
          </select>
        </div>
        <button onClick={generate} disabled={busy} className="flex items-center gap-2 rounded-full bg-[#2563EB] px-6 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#1D4ED8] disabled:opacity-60" data-testid="marketing-generate">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />} Generate kit
        </button>
      </div>

      {kit && (
        <div className="mt-6 grid gap-6 rounded-2xl border border-slate-200 bg-white p-6 md:grid-cols-[240px_1fr]" data-testid="marketing-kit">
          <div className="text-center">
            {link?.ok && link.qr_png ? (
              <img src={link.qr_png} alt="Property QR" className="mx-auto h-52 w-52 rounded-xl border border-slate-200 p-2" data-testid="marketing-qr" />
            ) : (
              <div className="mx-auto flex h-52 w-52 items-center justify-center rounded-xl border border-dashed border-slate-300 text-center text-xs text-slate-400">QR available once the link points to a live production URL</div>
            )}
            {link?.ok && (
              <div className="mt-3 flex justify-center gap-2">
                <a href={link.qr_png} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-[#0F172A] hover:bg-slate-50" data-testid="marketing-qr-png"><Download className="h-3.5 w-3.5" /> PNG</a>
                <a href={link.qr_svg} target="_blank" rel="noreferrer" className="flex items-center gap-1 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-bold text-[#0F172A] hover:bg-slate-50" data-testid="marketing-qr-svg"><Download className="h-3.5 w-3.5" /> SVG</a>
              </div>
            )}
          </div>
          <div>
            <div className="text-lg font-black text-[#0F172A]">{kit.listing.address || kit.listing.title}</div>
            <div className="text-sm text-slate-500">{kit.listing.city_name}, {kit.listing.state_name}{kit.listing.price ? ` · $${kit.listing.price.toLocaleString()}` : ""}</div>
            {link?.ok ? (
              <>
                <div className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-400">Branded short link</div>
                <div className="mt-1.5 flex items-center gap-2">
                  <input readOnly value={link.short_url} className="w-full rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-[#0F172A]" data-testid="marketing-short-url" />
                  <button onClick={copy} className="shrink-0 rounded-lg bg-[#0F172A] px-3 py-2 text-white" data-testid="marketing-copy">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</button>
                </div>
                <button onClick={loadStats} className="mt-4 flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-bold text-[#0F172A] hover:bg-slate-50" data-testid="marketing-stats-btn">
                  {stats === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <BarChart3 className="h-4 w-4" />} Scan analytics
                </button>

                {kit.modes && (
                  <div className="mt-5 rounded-xl border border-slate-200 p-4" data-testid="marketing-mode">
                    <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Dynamic QR destination</div>
                    <p className="mt-1 text-xs text-slate-500">Re-point this one printed code anytime — no reprinting.</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {Object.entries(kit.modes).map(([m, label]) => (
                        <button key={m} onClick={() => switchMode(m)}
                          className={`rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${mode === m ? "bg-[#2563EB] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                          data-testid={`marketing-mode-${m}`}>{label}</button>
                      ))}
                    </div>
                  </div>
                )}
                {stats && stats !== "loading" && (
                  <div className="mt-3 rounded-xl bg-[#F8FAFC] p-4" data-testid="marketing-stats">
                    {stats.ok ? (
                      <div className="flex flex-wrap items-center gap-6">
                        <Stat label="Total scans" value={stats.clicks} />
                        {(stats.top_countries || [])[0] && <Stat label="Top location" value={stats.top_countries[0].country || stats.top_countries[0].name || "—"} />}
                        {(stats.top_devices || [])[0] && <Stat label="Top device" value={stats.top_devices[0].device || stats.top_devices[0].name || "—"} />}
                        <button onClick={loadStats} className="ml-auto self-center text-slate-400 hover:text-[#2563EB]"><RefreshCw className="h-4 w-4" /></button>
                      </div>
                    ) : <span className="text-sm text-slate-500">No scans yet — analytics appear after the QR is scanned.</span>}
                  </div>
                )}
              </>
            ) : (
              <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{link?.message || "This kit will produce a branded QR once HomesPulse is on its live production domain (splink.pro rejects preview URLs)."}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const Stat = ({ label, value }) => (
  <div><div className="text-2xl font-black text-[#0F172A]">{typeof value === "object" ? "—" : (value ?? 0)}</div><div className="text-xs text-slate-500">{label}</div></div>
);
const Center = ({ children }) => <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">{children}</div>;
